// Leitura dos cartões, espelhando as regras do app (finapp):
//  - a fatura é NOMEADA pelo mês do vencimento (ADR 0102); `referenceMonth` é identidade interna
//    e continua decidindo em qual fatura a compra cai;
//  - o que cada cartão mostra num mês é a fatura que VENCE nele, senão a próxima com algo
//    (lib/homeSummary.ts), e o "Total em faturas" é a soma disso;
//  - limite usado = tudo que ainda não foi pago, em qualquer fatura (creditCardRepository.getUsedLimit);
//  - as compras da fatura se agrupam pelo mês de ORIGEM da compra (lib/invoiceOriginGroups.ts).
// Puro e testável.
import type { CategoryRow, CreditCardRow, InvoiceRow, TagRow, TransactionRow } from './types';
import type { CategorySlice } from './model';
import { addMonths, cap, monthLong } from './format';
import { daysUntil } from './recurring';
import { effectiveDueDate } from './businessDay';
import { buildInvoiceOriginRows, originMonthOf } from '@/lib/invoiceOriginGroups';

export const INVOICE_STATUS: Record<string, string> = {
  paid: 'Paga',
  open: 'Aberta',
  closed: 'Fechada',
  partial: 'Parcial',
  overdue: 'Vencida',
};

/** Singular e plural pro resumo "1 fechada, 2 abertas", na ordem do que pede atenção. */
const STATUS_WORDS: Record<string, [string, string]> = {
  overdue: ['vencida', 'vencidas'],
  closed: ['fechada', 'fechadas'],
  partial: ['parcial', 'parciais'],
  open: ['aberta', 'abertas'],
  paid: ['paga', 'pagas'],
};

const byDue = (a: InvoiceRow, b: InvoiceRow) => a.dueDate.localeCompare(b.dueDate);

/** Data em que a fatura PODE ser paga: a nominal, rolada pro próximo dia útil quando o
 * cartão tem "vencimento em dias úteis" (ADR 0082). É o que o app mostra e o que decide
 * se está atrasada — o mês da fatura continua saindo da nominal. */
export const payDate = (i: InvoiceRow, card: CreditCardRow | undefined): string =>
  effectiveDueDate(i.dueDate, card?.dueOnBusinessDay);
/** A nominal rolou? Então vale dizer na tela por que a data mudou. */
export const rolledDue = (i: InvoiceRow, card: CreditCardRow | undefined): boolean =>
  payDate(i, card) !== i.dueDate;

/** Mês que dá nome à fatura: o do vencimento nominal, como os bancos fazem. */
export const displayMonth = (i: InvoiceRow): string => i.dueDate.slice(0, 7);
export const remaining = (i: InvoiceRow): number => Math.max(i.totalAmount - i.paidAmount, 0);
/** O valor que a Home do app mostra: o que falta pagar; se quitada, o que foi pago. */
export const displayAmount = (i: InvoiceRow): number => (remaining(i) > 0 ? remaining(i) : Math.max(i.totalAmount, i.paidAmount));
export const isUnpaid = (i: InvoiceRow): boolean => i.totalAmount - i.paidAmount > 0.005;

export interface CardState {
  card: CreditCardRow;
  /** fatura que vence no mês · próxima com algo lançado · nada */
  state: 'current' | 'next' | 'empty';
  invoice: InvoiceRow | null;
  amount: number;
}

/** O que cada cartão mostra no mês, na ordem manual do app (port de resolveHomeCardSummaries). */
/** Fatura com compra ou pagamento. A tela do cartão no app grava faturas VAZIAS dos meses
 * vizinhos ao abrir (pré-carga), e elas chegam pelo backup/sync: sem este filtro a vazia vira
 * "a do mês" com R$ 0 e esconde a próxima com algo (mesma regra de lib/homeSummary.ts do app). */
export const withCharges = (i: InvoiceRow): boolean => i.totalAmount > 0 || i.paidAmount > 0;

export function cardStates(cards: CreditCardRow[], invoices: InvoiceRow[], month: string): CardState[] {
  const inMonth = invoices.filter(i => displayMonth(i) === month && withCharges(i));
  const later = invoices.filter(i => displayMonth(i) > month && withCharges(i));
  return cards.map(card => {
    const current = inMonth.filter(i => i.creditCardId === card.id).sort(byDue)[0];
    if (current) return { card, state: 'current', invoice: current, amount: displayAmount(current) };
    const next = later.filter(i => i.creditCardId === card.id).sort(byDue)[0];
    if (next) return { card, state: 'next', invoice: next, amount: displayAmount(next) };
    return { card, state: 'empty', invoice: null, amount: 0 };
  });
}

export interface Trend {
  kind: 'none' | 'noBase' | 'same' | 'lower' | 'higher';
  pct: number;
}

export interface MonthSummary {
  /** soma do que cada cartão mostra pra fatura que vence no mês */
  total: number;
  count: number;
  byStatus: Record<string, number>;
  trend: Trend;
}

/** Cabeçalho de "Meus cartões" do app: total do mês e a tendência contra o mês passado. */
export function monthSummary(states: CardState[], previous: CardState[]): MonthSummary {
  const current = states.filter(s => s.state === 'current');
  const total = current.reduce((sum, s) => sum + s.amount, 0);
  const before = previous.filter(s => s.state === 'current').reduce((sum, s) => sum + s.amount, 0);
  const byStatus: Record<string, number> = {};
  for (const s of current) if (s.invoice) byStatus[s.invoice.status] = (byStatus[s.invoice.status] ?? 0) + 1;
  const delta = total - before;
  const trend: Trend =
    total <= 0 ? { kind: 'none', pct: 0 }
    : before <= 0 ? { kind: 'noBase', pct: 0 }
    : Math.abs(delta) < 0.005 ? { kind: 'same', pct: 0 }
    : { kind: delta < 0 ? 'lower' : 'higher', pct: Math.round(Math.abs(delta / before) * 100) };
  return { total, count: current.length, byStatus, trend };
}

export function trendLabel(t: Trend): string {
  switch (t.kind) {
    case 'none': return 'Sem fatura vencendo neste mês';
    case 'noBase': return 'Sem base para comparar com o mês passado';
    case 'same': return 'Mesmo nível do mês passado';
    case 'lower': return `${t.pct}% menor que o mês passado`;
    case 'higher': return `${t.pct}% maior que o mês passado`;
  }
}

export function statusSummary(byStatus: Record<string, number>): string {
  return Object.keys(STATUS_WORDS)
    .filter(s => byStatus[s])
    .map(s => `${byStatus[s]} ${STATUS_WORDS[s][byStatus[s] === 1 ? 0 : 1]}`)
    .join(', ');
}

/** Limite tomado: o que falta pagar em QUALQUER fatura — parcela futura também consome limite. */
export function usedLimit(invoices: InvoiceRow[], cardId: string): number {
  return invoices.filter(i => i.creditCardId === cardId && isUnpaid(i)).reduce((sum, i) => sum + remaining(i), 0);
}

export interface CardOverview {
  used: number;
  available: number;
  usedPct: number;
  nextDue: { invoice: InvoiceRow; days: number } | null;
  overdueCount: number;
  /** soma das caixinhas vinculadas ao cartão ("limite garantido") */
  guaranteed: number;
  /** faturas que vencem depois do mês e já têm algo lançado, em ordem */
  ahead: InvoiceRow[];
  aheadTotal: number;
}

/** Limite disponível como o app (getAvailableLimit): limite + caixinhas que "garantem limite"
 * deste cartão − o que está a pagar. `guaranteed` é a soma dessas caixinhas. */
export function cardOverview(card: CreditCardRow, invoices: InvoiceRow[], month: string, today: string, guaranteed = 0): CardOverview {
  const mine = invoices.filter(i => i.creditCardId === card.id);
  const used = usedLimit(mine, card.id);
  const limit = Math.max(card.creditLimit, 0) + Math.max(guaranteed, 0);
  const unpaid = mine.filter(isUnpaid);
  // "Próxima" e "atrasada" vão pela data de pagamento: com dia útil ligado, uma fatura que
  // vence no sábado só fica atrasada depois da segunda.
  const due = (i: InvoiceRow) => payDate(i, card);
  const next = unpaid.filter(i => due(i) >= today).sort(byDue)[0];
  const ahead = mine.filter(i => displayMonth(i) > month && i.totalAmount > 0).sort(byDue);
  return {
    used,
    guaranteed: Math.max(guaranteed, 0),
    available: Math.max(limit - used, 0),
    usedPct: limit > 0 ? Math.min(100, Math.round((used / limit) * 100)) : 0,
    nextDue: next ? { invoice: next, days: daysUntil(due(next), today) } : null,
    overdueCount: unpaid.filter(i => due(i) < today).length,
    ahead,
    aheadTotal: ahead.reduce((sum, i) => sum + i.totalAmount, 0),
  };
}

/** Faturas do cartão em ordem cronológica até dois meses à frente: a faixa de meses e o histórico.
 * Fatura vazia do passado fica de fora (é só o slot do ciclo); a do mês corrente em diante entra
 * mesmo zerada, porque é ela que o cartão está mostrando lá em cima. */
export function invoiceTimeline(invoices: InvoiceRow[], cardId: string, today: string): InvoiceRow[] {
  const month = today.slice(0, 7);
  const horizon = addMonths(month, 2);
  return invoices
    .filter(i => i.creditCardId === cardId && displayMonth(i) <= horizon && (i.totalAmount > 0 || i.status === 'paid' || displayMonth(i) >= month))
    .sort(byDue);
}

/** As compras que compõem a fatura. O pagamento (feito ou projetado) também carrega
 * `invoiceId`, mas vem com `accountId` — e não é compra. */
export function invoiceItems(transactions: TransactionRow[], invoice: InvoiceRow): TransactionRow[] {
  return transactions.filter(t => t.invoiceId === invoice.id && t.creditCardId === invoice.creditCardId && !t.accountId);
}

export interface InvoiceStats {
  /** compras menos estornos */
  total: number;
  count: number;
  installments: number;
  recurring: number;
  biggest: TransactionRow | null;
}

export function invoiceStats(rows: TransactionRow[]): InvoiceStats {
  let biggest: TransactionRow | null = null;
  let total = 0;
  let installments = 0;
  let recurring = 0;
  for (const t of rows) {
    total += t.type === 'income' ? -t.amount : t.amount;
    if (t.installmentNumber) installments += 1;
    if (t.recurringId) recurring += 1;
    if (t.type === 'expense' && (!biggest || t.amount > biggest.amount)) biggest = t;
  }
  return { total, count: rows.length, installments, recurring, biggest };
}

export const NO_CATEGORY = '__none__';
export const NO_TAG = '__untagged__';

/** Compras da fatura por categoria, da maior pra menor. Estorno fica de fora. */
export function invoiceSlices(rows: TransactionRow[], categories: Map<string, CategoryRow>): CategorySlice[] {
  const acc = new Map<string, CategorySlice>();
  for (const t of rows) {
    if (t.type !== 'expense') continue;
    const cat = t.categoryId ? categories.get(t.categoryId) : undefined;
    const id = cat?.id ?? NO_CATEGORY;
    const slice = acc.get(id) ?? { id, name: cat?.name ?? 'Sem categoria', color: cat?.color ?? '#9CA3AF', total: 0, count: 0 };
    slice.total += t.amount;
    slice.count += 1;
    acc.set(id, slice);
  }
  return [...acc.values()].filter(s => s.total > 0).sort((a, b) => b.total - a.total);
}

/** Por tag, como o app: compra com 2+ tags conta em CADA uma (sem rateio); sem tag vira
 * "Sem tag" pra fechar com a fatura inteira. */
export function tagSlices(rows: TransactionRow[], tagsOf: (id: string) => TagRow[]): CategorySlice[] {
  const acc = new Map<string, CategorySlice>();
  let untagged = 0;
  let untaggedCount = 0;
  for (const t of rows) {
    if (t.type !== 'expense') continue;
    const tags = tagsOf(t.id);
    if (!tags.length) { untagged += t.amount; untaggedCount += 1; continue; }
    for (const tag of tags) {
      const slice = acc.get(tag.id) ?? { id: tag.id, name: tag.name, color: tag.color, total: 0, count: 0 };
      slice.total += t.amount;
      slice.count += 1;
      acc.set(tag.id, slice);
    }
  }
  const out = [...acc.values()].sort((a, b) => b.total - a.total);
  if (untagged > 0) out.push({ id: NO_TAG, name: 'Sem tag', color: '#8C9CA2', total: untagged, count: untaggedCount });
  return out;
}

export interface OriginGroup {
  /** mês de origem da compra (yyyy-MM) */
  month: string;
  /** compras desta competência (avulsas e parcela 1) */
  isCurrent: boolean;
  rows: TransactionRow[];
  total: number;
}

/** Agrupa pelo mês em que a compra foi feita: o que é desta fatura primeiro, depois cada
 * mês anterior que ainda está sendo pago em parcelas. Ordem dentro do grupo preservada. */
export function groupByOrigin(rows: TransactionRow[], referenceMonth: string): OriginGroup[] {
  const groups: OriginGroup[] = [];
  let current: OriginGroup | null = null;
  for (const row of buildInvoiceOriginRows(rows, referenceMonth)) {
    if (row.kind === 'header') {
      current = { month: row.originMonth, isCurrent: row.isCurrent, rows: [], total: 0 };
      groups.push(current);
      continue;
    }
    if (!current) {
      // Um grupo só: a lib não intercala cabeçalho, mas a tela sempre nomeia o bloco — e o
      // bloco único pode ser todo de parcelas antigas (fatura com uma linha "7/9").
      const month = originMonthOf(row.transaction, referenceMonth);
      current = { month, isCurrent: month === referenceMonth, rows: [], total: 0 };
      groups.push(current);
    }
    current.rows.push(row.transaction);
    current.total += row.transaction.type === 'income' ? -row.transaction.amount : row.transaction.amount;
  }
  return groups;
}

/** "Compras desta fatura" · "Parcelas de abril" · "Parcelas de dezembro de 2024". */
export function originLabel(group: Pick<OriginGroup, 'month' | 'isCurrent'>, today: string): string {
  if (group.isCurrent) return 'Compras desta fatura';
  const sameYear = group.month.slice(0, 4) === today.slice(0, 4);
  return `Parcelas de ${sameYear ? monthLong(group.month).split(' de ')[0] : monthLong(group.month)}`;
}

/** "Fatura de setembro de 2026" — sempre pelo mês do vencimento. */
export function invoiceName(i: InvoiceRow): string {
  return `Fatura de ${monthLong(displayMonth(i))}`;
}

export function invoiceNameCap(i: InvoiceRow): string {
  return cap(monthLong(displayMonth(i)));
}
